import { Suspense, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { PageSkeleton } from "@/shared/components/PageSkeleton";
import { usePreference } from "@/shared/hooks/usePreference";
import { cn } from "@/shared/ui/cn";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

const parseBool = (raw: string) => (raw === "true" ? true : raw === "false" ? false : null);

/** Sidebar + top bar + content (max-width 1440, left-aligned). Sidebar becomes a drawer under 1024 px. */
export function AppShell() {
  const location = useLocation();
  const [collapsed, setCollapsed] = usePreference("nacs-internal.sidebar-collapsed", false, parseBool);
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-dvh bg-bg text-fg">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        className="sticky top-0 hidden h-dvh lg:flex"
      />
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-[oklch(0.1_0.01_250/0.45)]"
            onClick={() => setDrawerOpen(false)}
          />
          <Sidebar
            collapsed={false}
            drawer
            onToggle={() => undefined}
            onNavigate={() => setDrawerOpen(false)}
            className="relative z-10 h-dvh shadow-sm"
          />
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenMenu={() => setDrawerOpen(true)} />
        <main id="main" className={cn("w-full max-w-[1440px] flex-1 px-4 py-5 md:px-6")}>
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
